import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Shield, AlertTriangle, ChevronDown } from 'lucide-react';

function NDAModal({ onAccept, isReaccept = false, previousVersion = null }) {
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('1.0');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    // Fetch current NDA version
    const fetchVersion = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nda/current-version`);
        if (response.ok) {
          const data = await response.json();
          setCurrentVersion(data.version);
        }
      } catch (error) {
        console.error('Failed to fetch NDA version');
      }
    };
    fetchVersion();

    // Trap focus within modal for accessibility
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle scroll detection
  const handleScroll = (e) => {
    const container = e.target;
    const scrolledToBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 20;
    if (scrolledToBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const handleAccept = async () => {
    if (!hasScrolledToBottom) {
      toast.error('Please scroll to the bottom of the agreement to read all terms');
      return;
    }
    if (!accepted || !signature) {
      toast.error('Please check the box and enter your name to accept');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nda/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ version: currentVersion })
      });

      if (response.ok) {
        toast.success('Agreement accepted successfully');
        onAccept();
      } else {
        toast.error('Failed to accept agreement');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4" 
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="nda-title"
      aria-describedby="nda-description"
      data-testid="nda-modal"
    >
      <div 
        className="bg-white rounded-xl w-full overflow-hidden flex flex-col"
        style={{ 
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          maxWidth: '800px',
          maxHeight: '90vh',
          width: '95%'
        }}
        role="document"
      >
        {/* Header */}
        <div style={{ backgroundColor: '#1B5E5F', padding: '20px 32px' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div style={{ 
                backgroundColor: 'rgba(255,255,255,0.15)', 
                borderRadius: '12px', 
                padding: '12px' 
              }}>
                <Shield size={28} color="#FFFFFF" aria-hidden="true" />
              </div>
              <div>
                <h2 
                  id="nda-title"
                  style={{ 
                    color: '#FFFFFF', 
                    fontSize: '20px', 
                    fontWeight: '700',
                    marginBottom: '4px'
                  }}
                >
                  Responsible Use of Microsoft Compete Assets
                </h2>
                <p 
                  id="nda-description"
                  style={{ color: '#B2DFDB', fontSize: '14px' }}
                >
                  Microsoft Confidential - Usage Agreement
                </p>
              </div>
            </div>
            <div style={{ 
              backgroundColor: 'rgba(255,255,255,0.1)', 
              borderRadius: '8px', 
              padding: '8px 16px',
              textAlign: 'center'
            }}>
              <span style={{ color: '#B2DFDB', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Version
              </span>
              <p style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: '700' }}>
                {currentVersion}
              </p>
            </div>
          </div>
          
          {/* Re-acceptance Warning */}
          {isReaccept && (
            <div 
              style={{ 
                marginTop: '16px', 
                padding: '12px 16px', 
                backgroundColor: '#FEF3C7', 
                borderRadius: '8px',
                border: '1px solid #F59E0B',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
              role="alert"
            >
              <AlertTriangle size={20} style={{ color: '#B45309', flexShrink: 0 }} aria-hidden="true" />
              <div>
                <p style={{ color: '#92400E', fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>
                  Agreement Updated - Re-acceptance Required
                </p>
                <p style={{ color: '#92400E', fontSize: '12px' }}>
                  Terms updated from version {previousVersion || 'previous'} to {currentVersion}. Please review and accept.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Agreement Content - Scrollable */}
        <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{ 
              padding: '20px 24px',
              height: '100%',
              maxHeight: '280px',
              overflowY: 'auto',
              backgroundColor: '#FFFFFF'
            }}
            data-testid="nda-agreement-text"
            tabIndex={0}
            aria-label="Agreement Terms - Please scroll to read all terms"
          >
            {/* Main Warning - Red Text */}
            <div style={{ marginBottom: '24px' }}>
              <p style={{ 
                color: '#DC2626', 
                fontSize: '15px', 
                lineHeight: '1.7', 
                fontWeight: '600',
                marginBottom: '16px'
              }}>
                This content is intended solely for internal partner and Microsoft use, and for the purpose of readiness and product knowledge. It contains confidential information and competitive insights compiled from publicly researched sources.
              </p>
              
              <p style={{ 
                color: '#DC2626', 
                fontSize: '15px', 
                lineHeight: '1.7', 
                fontWeight: '700'
              }}>
                It is not authorized for external distribution, customer-facing communications, or marketing activities. Any use by a partner that does not align to these expectations will be considered unauthorized use and at partner's own risk.
              </p>
            </div>

            {/* Guidelines List */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '20px', paddingLeft: '8px' }}>
                <p style={{ 
                  color: '#1F2937', 
                  fontSize: '14px', 
                  fontWeight: '700',
                  marginBottom: '6px'
                }}>
                  • Do not share compete assets directly with customers or external audiences.
                </p>
                <p style={{ 
                  color: '#4B5563', 
                  fontSize: '14px', 
                  lineHeight: '1.6',
                  paddingLeft: '14px'
                }}>
                  Use the insights within to guide compliant, value-driven discussions.
                </p>
              </div>

              <div style={{ marginBottom: '20px', paddingLeft: '8px' }}>
                <p style={{ 
                  color: '#1F2937', 
                  fontSize: '14px', 
                  fontWeight: '700',
                  marginBottom: '6px'
                }}>
                  • Represent all information exactly as provided.
                </p>
                <p style={{ 
                  color: '#4B5563', 
                  fontSize: '14px', 
                  lineHeight: '1.6',
                  paddingLeft: '14px'
                }}>
                  Do not modify, alter, or misrepresent any data, visuals, or claims. Maintain accuracy, credibility, and compliance with Microsoft's standards.
                </p>
              </div>

              <div style={{ marginBottom: '20px', paddingLeft: '8px' }}>
                <p style={{ 
                  color: '#1F2937', 
                  fontSize: '14px', 
                  fontWeight: '700',
                  marginBottom: '6px'
                }}>
                  • Always use the latest compete assets.
                </p>
                <p style={{ 
                  color: '#4B5563', 
                  fontSize: '14px', 
                  lineHeight: '1.6',
                  paddingLeft: '14px'
                }}>
                  Check the timestamp on each asset to ensure you are using the latest version. Content is refreshed frequently.
                </p>
              </div>

              <div style={{ marginBottom: '20px', paddingLeft: '8px' }}>
                <p style={{ 
                  color: '#1F2937', 
                  fontSize: '14px', 
                  fontWeight: '700',
                  marginBottom: '6px'
                }}>
                  • Comply with local laws and regulations.
                </p>
                <p style={{ 
                  color: '#4B5563', 
                  fontSize: '14px', 
                  lineHeight: '1.6',
                  paddingLeft: '14px'
                }}>
                  The laws and regulations related to comparative advertising differ by country. Please familiarize yourself with those that are relevant to your location and circumstances.
                </p>
              </div>
            </div>

            {/* Partner Value Statement */}
            <div style={{ 
              padding: '16px 20px', 
              backgroundColor: '#F0F9FF', 
              borderRadius: '8px',
              borderLeft: '4px solid #0284C7',
              marginBottom: '24px'
            }}>
              <p style={{ 
                color: '#0C4A6E', 
                fontSize: '14px', 
                lineHeight: '1.7',
                fontWeight: '500'
              }}>
                Partners are a critical part of the value equation. Your expertise, services, and ongoing support help customers realize the full value of Microsoft's products.
              </p>
            </div>

            {/* Footer Warning */}
            <div style={{ 
              padding: '12px 16px', 
              backgroundColor: '#DC2626', 
              borderRadius: '6px',
              marginBottom: '16px'
            }}>
              <p style={{ 
                color: '#FFFFFF', 
                fontSize: '12px', 
                lineHeight: '1.5',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                MICROSOFT CONFIDENTIAL. DO NOT SHARE WITH CUSTOMERS OR USE AS MARKETING MATERIAL.
              </p>
            </div>

            {/* Compliance Notice */}
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#FEF3C7', 
              borderRadius: '8px',
              border: '1px solid #F59E0B'
            }}>
              <p style={{ 
                color: '#92400E', 
                fontSize: '13px', 
                lineHeight: '1.6',
                marginBottom: '8px'
              }}>
                <strong>Re-Confirmation Required:</strong> You will be required to re-accept this agreement every 30 days to maintain access to compete assets.
              </p>
              <p style={{ 
                color: '#92400E', 
                fontSize: '13px', 
                lineHeight: '1.6'
              }}>
                <strong>Accountability:</strong> Your usage activity may be monitored, logged, and attributed to your user identity. Unauthorized sharing or misuse may result in access removal and reporting to your organization leadership.
              </p>
            </div>

            {/* End marker */}
            <div style={{ 
              textAlign: 'center', 
              padding: '16px',
              marginTop: '20px',
              backgroundColor: hasScrolledToBottom ? '#D1FAE5' : '#FEF3C7',
              borderRadius: '8px',
              border: hasScrolledToBottom ? '1px solid #10B981' : '1px solid #F59E0B'
            }}>
              {hasScrolledToBottom ? (
                <p style={{ color: '#065F46', fontSize: '13px', fontWeight: '500' }}>
                  ✓ You have read the complete agreement
                </p>
              ) : (
                <p style={{ color: '#92400E', fontSize: '13px', fontWeight: '500' }}>
                  Please scroll to read the complete agreement
                </p>
              )}
            </div>
          </div>

          {/* Scroll indicator */}
          {!hasScrolledToBottom && (
            <button
              onClick={scrollToBottom}
              style={{
                position: 'absolute',
                bottom: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#1B5E5F',
                color: 'white',
                border: 'none',
                borderRadius: '20px',
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                boxShadow: '0 4px 12px rgba(27, 94, 95, 0.4)'
              }}
            >
              Scroll to continue <ChevronDown size={14} />
            </button>
          )}
        </div>

        {/* Acceptance Section */}
        <div style={{ 
          padding: '20px 24px', 
          backgroundColor: '#F9FAFB',
          borderTop: '1px solid #E5E7EB',
          flexShrink: 0
        }}>
          {/* Checkbox and label - fixed layout */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '12px',
              cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed',
              opacity: hasScrolledToBottom ? 1 : 0.5
            }}>
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => hasScrolledToBottom && setAccepted(e.target.checked)}
                disabled={!hasScrolledToBottom}
                data-testid="nda-accept-checkbox"
                aria-describedby="checkbox-label"
                style={{ 
                  width: '18px',
                  height: '18px',
                  minWidth: '18px',
                  marginTop: '3px',
                  accentColor: '#0C6A1D',
                  cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
                }}
              />
              <label 
                id="checkbox-label"
                onClick={() => hasScrolledToBottom && setAccepted(!accepted)}
                style={{ 
                  color: '#374151', 
                  fontSize: '13px', 
                  lineHeight: '1.5',
                  cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
                }}
              >
                I have read and understood the Microsoft Compete Assets usage terms above (Version {currentVersion}), and I agree to comply with all confidentiality and permitted-use requirements.
              </label>
            </div>
          </div>

          {/* Signature input */}
          <div style={{ marginBottom: '16px' }}>
            <label 
              htmlFor="signature-input"
              style={{ 
                display: 'block', 
                fontSize: '13px', 
                fontWeight: '600', 
                color: '#374151',
                marginBottom: '6px'
              }}
            >
              Type your full name to confirm:
            </label>
            <input
              id="signature-input"
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              disabled={!hasScrolledToBottom}
              data-testid="nda-signature-input"
              placeholder="Your Full Name"
              aria-required="true"
              style={{ 
                width: '100%',
                padding: '10px 14px',
                border: '2px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#1F2937',
                backgroundColor: hasScrolledToBottom ? '#FFFFFF' : '#F3F4F6',
                cursor: hasScrolledToBottom ? 'text' : 'not-allowed',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <button
            onClick={handleAccept}
            disabled={!hasScrolledToBottom || !accepted || !signature || loading}
            data-testid="nda-accept-button"
            aria-disabled={!hasScrolledToBottom || !accepted || !signature || loading}
            style={{ 
              width: '100%',
              padding: '12px',
              borderRadius: '6px',
              border: 'none',
              fontSize: '14px',
              fontWeight: '600',
              color: '#FFFFFF',
              backgroundColor: (!hasScrolledToBottom || !accepted || !signature || loading) ? '#9CA3AF' : '#0C6A1D',
              cursor: (!hasScrolledToBottom || !accepted || !signature || loading) ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s'
            }}
          >
            {loading ? 'Processing...' : `I Agree to Version ${currentVersion}`}
          </button>

          <p style={{ 
            fontSize: '11px', 
            color: '#6B7280', 
            textAlign: 'center', 
            marginTop: '12px',
            lineHeight: '1.5'
          }}>
            Your acceptance will be recorded with timestamp, IP address, and version number for compliance tracking.
            <br />
            <span style={{ color: '#9CA3AF' }}>This agreement is valid for 30 days from acceptance date.</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default NDAModal;
